import type { Snapshot } from "./types";
const epochs = new Map<string, number>();
let dbPromise: Promise<IDBDatabase> | undefined;
function database() {
  return (dbPromise ??= new Promise((resolve, reject) => {
    const request = indexedDB.open("visra-wall", 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore("snapshots");
      request.result.createObjectStore("blobs");
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }));
}
async function get(store: string, key: string): Promise<any> {
  const db = await database();
  return new Promise((resolve, reject) => {
    const r = db.transaction(store).objectStore(store).get(key);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
async function put(
  store: string,
  key: string,
  value: unknown,
  valid: () => boolean = () => true,
) {
  const db = await database();
  if (!valid()) return;
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, "readwrite");
    tx.objectStore(store).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
export async function cachedSnapshot(key: string) {
  return get("snapshots", key).catch(() => null) as Promise<Snapshot | null>;
}
export async function clearCache(key: string) {
  epochs.set(key, (epochs.get(key) || 0) + 1);
  const db = await database().catch(() => null);
  if (!db) return;
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(["snapshots", "blobs"], "readwrite");
    tx.objectStore("snapshots").delete(key);
    const r = tx.objectStore("blobs").openCursor();
    r.onsuccess = () => {
      const c = r.result;
      if (c) {
        if (String(c.key).startsWith(key + ":")) c.delete();
        c.continue();
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
export async function cacheSnapshot(key: string, snapshot: Snapshot) {
  const epoch = (epochs.get(key) || 0) + 1;
  epochs.set(key, epoch);
  const valid = () => epochs.get(key) === epoch;
  const compact = { ...snapshot, photos: snapshot.photos.slice(-150) };
  await put("snapshots", key, compact, valid).catch(() => {});
  const ids = new Set(
    [...compact.photos, ...compact.assets].map((m) => key + ":" + m.id),
  );
  const db = await database().catch(() => null);
  if (db && valid()) {
    const tx = db.transaction("blobs", "readwrite");
    const r = tx.objectStore("blobs").openCursor();
    r.onsuccess = () => {
      const c = r.result;
      if (c) {
        if (String(c.key).startsWith(key + ":") && !ids.has(String(c.key)))
          c.delete();
        c.continue();
      }
    };
  }
  for (const m of [...compact.assets, ...compact.photos].reverse()) {
    try {
      if (!valid()) return;
      if (await get("blobs", key + ":" + m.id)) continue;
      const r = await fetch(m.url, { signal: AbortSignal.timeout(15000) });
      if (r.ok) {
        const blob = await r.blob();
        if (blob.size < 30 * 1024 * 1024)
          await put("blobs", key + ":" + m.id, blob, valid);
      }
    } catch {
      /* Keep the running wall even when storage quota/network is exhausted. */
    }
  }
}
export async function resolveCached(key: string, id: string, fallback: string) {
  const blob = await get("blobs", key + ":" + id).catch(() => null);
  return blob ? URL.createObjectURL(blob) : fallback;
}
